import React, { FC } from 'react';

import { RoverLoading } from '@rover/react-lib/src/components';
import {
  apiTrainingSearchRetrieve,
  getApiTrainingSearchRetrieveQueryKey,
  useApiTrainingSearchRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-training-search-retrieve';
import type { ApiTrainingSearchRetrieveParams } from '@rover/rsdk/src/apiClient/latest/model/apiTrainingSearchRetrieveParams';
import { LocalityTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/localityTypeEnum';
import type { SeoLocation } from '@rover/rsdk/src/apiClient/latest/model/seoLocation';
import { captureMessage } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { getPath } from '@rover/shared/js/utils/getFullUrl';
import { Http404Error, Http500Error } from '@rover/utilities/HttpError';

import TrainingSeoLocationPageTemplate from '../../templates/TrainingSeoLocation/TrainingSeoLocationPageTemplate';
import { SeoLocationContainerProps, SeoLocationEngineContainer } from '../../types';
import { handleSeoLocationQueryError } from '../../utils/handleSeoLocationQueryError';
import { useSeoLocationSharedQueries } from '../../utils/seoLocationSharedQueries';

const getTrainingSearchParams = (seoLocation: SeoLocation): ApiTrainingSearchRetrieveParams => ({
  seoLocationSlug: seoLocation.details.searchParams.seoLocationSlug,
});

const assertIsCity = (seoLocation: SeoLocation): void => {
  if (seoLocation.localityType !== LocalityTypeEnum.city) {
    throw new Http404Error();
  }
};

const TrainingSeoLocationContainer: FC<SeoLocationContainerProps> = ({ seoLocation }) => {
  assertIsCity(seoLocation);

  const searchQuery = useApiTrainingSearchRetrieve(getTrainingSearchParams(seoLocation));
  const {
    countryConfig,
    frontendConfig,
    isLoading: isLoadingShared,
    isError: isErrorShared,
    queryTags,
  } = useSeoLocationSharedQueries();

  if (searchQuery.isError || isErrorShared) {
    // 410s should be handled during prefetch, but just in case, also handle them here
    if (searchQuery.error) {
      handleSeoLocationQueryError(searchQuery.error);
    }

    captureMessage('Unhandled error loading SeoLocationPage', {
      tags: {
        pathname: getPath(),
        ...queryTags,
        isErrorTrainingSearch: searchQuery.isError,
      },
    });
    throw new Http500Error();
  }

  const isLoading = searchQuery.isLoading || isLoadingShared;
  if (isLoading) {
    if (process.env.JS_ENV_CLIENT) {
      // We shouldn't ever show a loading screen in the browser
      captureMessage('SeoLocationPageContainer rendered with missing prefetches', {
        tags: {
          pathname: getPath(),
          ...queryTags,
          isLoadingTrainingSearch: searchQuery.isLoading,
        },
      });
    }

    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  if (!searchQuery.data || !countryConfig || !frontendConfig) {
    throw new Http500Error();
  }

  return (
    <TrainingSeoLocationPageTemplate
      seoLocation={seoLocation}
      searchResponse={searchQuery.data}
      countryConfig={countryConfig}
      frontendConfig={frontendConfig}
    />
  );
};

const reactQueryPrefetch: SeoLocationEngineContainer['reactQueryPrefetch'] = async (
  queryClient,
  { seoLocation }
) => {
  assertIsCity(seoLocation);

  const trainingSearchParams = getTrainingSearchParams(seoLocation);
  return queryClient
    .fetchQuery({
      queryKey: getApiTrainingSearchRetrieveQueryKey(trainingSearchParams),
      queryFn: () => apiTrainingSearchRetrieve(trainingSearchParams),
    })
    .catch(handleSeoLocationQueryError);
};

export default Object.assign(TrainingSeoLocationContainer, { reactQueryPrefetch });
